'use strict';
Object.defineProperty(exports, "__esModule", { value: true });
exports.AppFeatureService = void 0;
const tenant_settings_model_1 = require("../../models/tenant-settings.model");
const store_manager_1 = require("../../managers/store.manager");
const cms_interface_1 = require("../../cms.interface");
const app_feature_interface_1 = require("./app-feature.interface");
const app_feature_map_1 = require("./app-feature.map");
const app_feature_model_1 = require("./app-feature.model");
class AppFeatureService {
    constructor(config) {
        var _a;
        this.config = config;
        this.tenantConfig = new tenant_settings_model_1.TenantConfig((_a = this.config) === null || _a === void 0 ? void 0 : _a.settings);
    }
    get returnNullIfNotFound() {
        var _a, _b;
        return (_b = (_a = this.config) === null || _a === void 0 ? void 0 : _a.options) === null || _b === void 0 ? void 0 : _b.returnNullIfNotFound;
    }
    async fetch(category) {
        const store = new store_manager_1.StoreManager(this.tenantConfig, cms_interface_1.MODULE.APP_FEATURES, category);
        const response = await store.get();
        const features = new app_feature_model_1.AppFeatures(...response.map(feature => {
            return new app_feature_model_1.AppFeature(feature);
        }));
        if (category) {
            features.mergeFeatures(app_feature_map_1.OVERRIDE_FEATURES.get(category) || []);
        }
        else {
            features.mergeFeatures(app_feature_map_1.OVERRIDE_FEATURES.get(app_feature_interface_1.APP_FEATURE_CATEGORY.ADMIN) || []);
            features.mergeFeatures(app_feature_map_1.OVERRIDE_FEATURES.get(app_feature_interface_1.APP_FEATURE_CATEGORY.GENERAL) || []);
            features.mergeFeatures(app_feature_map_1.OVERRIDE_FEATURES.get(app_feature_interface_1.APP_FEATURE_CATEGORY.CUSTOMER) || []);
        }
        return features;
    }
    /**
     * ### override()
     * #### Override Features
     *
     * This method will only merge features upon successful fetch
     * - this will `only` happen after http request or retrieving cache or memoized request
     * - `process.env.NODE_ENV` must be `development`
     *
     * if the feature is **existing**
     * - it will override its respective `value` and `props`, including `enabled` flag
     * - Setting `props` will only append new if not found and override existing if found
     * - Props merging is the same as feature merging which overrides `value`, `enabled` flag, and `props` if its a child prop
     *
     * if the feature is **new**
     * - it will be added along with existing features
     *
     * Example:
     * ```ts
     * const featureInstance = new AppFeature({
     *    name: "x",
     *    category: "customer",
     *    enabled: false
     * });
     *
     * const featureObject = {
     *   name: "Foo",
     *   category: "admin",
     *   enabled: true,
     *   value: { x: "y" }
     * };
     *
     * AppFeatureService.override([
     *   featureInstance,
     *   featureObject
     * ]);
     *
     * ```
     * @param features - App features to merge. Either object or App Feature instance
     */
    static override(features) {
        if (process.env.NODE_ENV === 'development') {
            const appfeatures = features.map((feature) => {
                if (feature instanceof app_feature_model_1.AppFeature) {
                    return feature;
                }
                else {
                    return new app_feature_model_1.AppFeature(feature);
                }
            });
            const filter = (category) => {
                return appfeatures.filter(feature => feature.category === category);
            };
            app_feature_map_1.OVERRIDE_FEATURES.set(app_feature_interface_1.APP_FEATURE_CATEGORY.ADMIN, filter(app_feature_interface_1.APP_FEATURE_CATEGORY.ADMIN));
            app_feature_map_1.OVERRIDE_FEATURES.set(app_feature_interface_1.APP_FEATURE_CATEGORY.GENERAL, filter(app_feature_interface_1.APP_FEATURE_CATEGORY.GENERAL));
            app_feature_map_1.OVERRIDE_FEATURES.set(app_feature_interface_1.APP_FEATURE_CATEGORY.CUSTOMER, filter(app_feature_interface_1.APP_FEATURE_CATEGORY.CUSTOMER));
        }
    }
    /**
     * ### resetOverride()
     * #### Reset Override
     * This method will reset the features to merge
     *
     * if `category` is provided, it will only reset the respective features
     *
     * @param category - App feature category
     */
    static resetOverride(category) {
        (0, app_feature_map_1.resetOverride)(category);
    }
    /**
     * ### getFeatureList()
     * This method will get all app features `general`, `admin`, and `customer`
     *
     * @returns {AppFeatures} All app features
     */
    async getFeatureList() {
        return await this.fetch();
    }
    /**
     * ### getAppFeature()
     * This method will get app feature by name and category
     *
     * if `returnNullIfNotFound` is set in the AppFeatureService({ options })
     * - it will return null when feature is not found
     *
     * Otherwise, by default
     * - it will create an `AppFeature` instance with default values and methods
     * which you can still access `isEnabled()`, `getValue()`, or `getProps()` methods or values of `name`, `category`, `enabled`, and `value`, or `id` (which is undefined)
     * - justification: application can still operate with the default behavior despite feature does not exist, especially we have multi tenant setup and some features might not be available in other countries
     *
     * @param name - App feature name
     * @param category - App Feature category
     */
    async getAppFeature(name, category) {
        const features = await this.fetch(category);
        const feature = features.find(feature => {
            return feature.name === name;
        });
        if (feature)
            return feature;
        if (this.returnNullIfNotFound) {
            return null;
        }
        return new app_feature_model_1.AppFeature({ name: name, category: category });
    }
    /**
     * ### getGeneralFeatureList()
     * This method will get app features by `general` category
     *
     * @returns {AppFeatures} General App Features
     */
    async getGeneralFeatureList() {
        return await this.fetch(app_feature_interface_1.APP_FEATURE_CATEGORY.GENERAL);
    }
    /**
     * ### getAdminFeatureList()
     * This method will get app features by `admin` category
     * @returns {AppFeatures} Admin App Features
     */
    async getAdminFeatureList() {
        return await this.fetch(app_feature_interface_1.APP_FEATURE_CATEGORY.ADMIN);
    }
    /**
     * ### getCustomerFeatureList()
     * This method will get app features by `customer` category
     *
     * @returns {AppFeatures} Customer App Features
     */
    async getCustomerFeatureList() {
        return await this.fetch(app_feature_interface_1.APP_FEATURE_CATEGORY.CUSTOMER);
    }
}
exports.AppFeatureService = AppFeatureService;
//# sourceMappingURL=app-feature.service.js.map